import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Settings,
  RefreshCw,
  Edit,
  DollarSign,
  CheckCircle,
  XCircle,
  Tag,
  Plus,
  Trash2,
  Copy,
} from "lucide-react";
import { format, subDays } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface PromoCode {
  id: string;
  code: string;
  salesperson_id: string;
  commission_rate: number | null;
  is_active: boolean;
  usage_count: number;
  created_at: string;
}

type RecurringType = "percentage" | "flat" | "none";

interface SalespersonRow {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  recurring_type: RecurringType;
  commission_rate: number;
  subscription_commission_rate: number | null;
  prescription_commission_rate: number | null;
  flat_fee_cents: number;
  recurring_flat_cents: number;
  monthly_target: number;
  is_active: boolean;
  notes: string | null;
  totalRevenue: number;
  totalCommission: number;
  orderCount: number;
  referralClicks: number;
}

interface PayoutRow {
  id: string;
  salesperson_id: string;
  salesperson_name: string;
  amount_cents: number;
  period_start: string;
  period_end: string;
  status: string;
  paid_at: string | null;
  commission_structure: string | null;
  notes: string | null;
  created_at: string;
}

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SalespersonManagement = () => {
  const [salespeople, setSalespeople] = useState<SalespersonRow[]>([]);
  const [payouts, setPayouts] = useState<PayoutRow[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [editSp, setEditSp] = useState<SalespersonRow | null>(null);
  const [editFlatFee, setEditFlatFee] = useState("");
  const [editRecurringType, setEditRecurringType] = useState<RecurringType>("percentage");
  const [editRate, setEditRate] = useState("");
  const [editSubRate, setEditSubRate] = useState("");
  const [editRxRate, setEditRxRate] = useState("");
  const [editPerTypeRates, setEditPerTypeRates] = useState(false);
  const [editRecurringFlat, setEditRecurringFlat] = useState("");
  const [editTarget, setEditTarget] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [payoutView, setPayoutView] = useState(false);
  const [promoView, setPromoView] = useState<string | null>(null); // salesperson user_id
  const [newPromoCode, setNewPromoCode] = useState("");
  const [newPromoRate, setNewPromoRate] = useState("");
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [rolesRes, profilesRes, settingsRes, ordersRes, emailRes, payoutsRes, promoRes, clicksRes] = await Promise.all([
      supabase.from("user_roles").select("user_id, role").eq("role", "salesperson"),
      supabase.from("profiles").select("user_id, first_name, last_name"),
      supabase.from("salesperson_settings").select("*"),
      supabase.from("orders").select("salesperson_id, amount_cents, commission_rate, status").not("salesperson_id", "is", null),
      supabase.functions.invoke("list-user-emails"),
      supabase.from("commission_payouts").select("*").order("created_at", { ascending: false }),
      supabase.from("promo_codes").select("*").order("created_at", { ascending: false }),
      supabase.from("referral_clicks").select("referral_code"),
    ]);

    const spIds = (rolesRes.data || []).map((r: any) => r.user_id);
    const profileMap = new Map((profilesRes.data || []).map((p: any) => [p.user_id, p]));
    const settingsMap = new Map((settingsRes.data || []).map((s: any) => [s.user_id, s]));
    const emailMap: Record<string, string> = emailRes.data?.emails || {};

    const orderAgg = new Map<string, { rev: number; comm: number; count: number }>();
    (ordersRes.data || []).forEach((o: any) => {
      if (!["cancelled", "refunded"].includes(o.status)) {
        const agg = orderAgg.get(o.salesperson_id) || { rev: 0, comm: 0, count: 0 };
        agg.rev += o.amount_cents;
        agg.comm += (o.amount_cents * o.commission_rate) / 100;
        agg.count += 1;
        orderAgg.set(o.salesperson_id, agg);
      }
    });

    // Aggregate referral clicks per salesperson (referral_code = user_id)
    const clickAgg = new Map<string, number>();
    (clicksRes.data || []).forEach((c: any) => {
      clickAgg.set(c.referral_code, (clickAgg.get(c.referral_code) || 0) + 1);
    });

    const rows: SalespersonRow[] = spIds.map((id: string) => {
      const profile = profileMap.get(id);
      const settings = settingsMap.get(id);
      const agg = orderAgg.get(id) || { rev: 0, comm: 0, count: 0 };
      return {
        user_id: id,
        first_name: profile?.first_name || null,
        last_name: profile?.last_name || null,
        email: emailMap[id] || null,
        recurring_type: (settings?.recurring_type as RecurringType) || "percentage",
        commission_rate: settings?.commission_rate ?? 10,
        subscription_commission_rate: settings?.subscription_commission_rate ?? null,
        prescription_commission_rate: settings?.prescription_commission_rate ?? null,
        flat_fee_cents: settings?.flat_fee_cents ?? 0,
        recurring_flat_cents: settings?.recurring_flat_cents ?? 0,
        monthly_target: settings?.monthly_target ?? 0,
        is_active: settings?.is_active ?? true,
        notes: settings?.notes || null,
        totalRevenue: agg.rev,
        totalCommission: agg.comm,
        orderCount: agg.count,
        referralClicks: clickAgg.get(id) || 0,
      };
    });

    setSalespeople(rows.sort((a, b) => b.totalRevenue - a.totalRevenue));

    const payoutRows: PayoutRow[] = (payoutsRes.data || []).map((p: any) => {
      const profile = profileMap.get(p.salesperson_id);
      return {
        ...p,
        salesperson_name: profile
          ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || p.salesperson_id.slice(0, 8)
          : p.salesperson_id.slice(0, 8),
      };
    });
    setPayouts(payoutRows);
    setPromoCodes((promoRes.data as PromoCode[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openEdit = (sp: SalespersonRow) => {
    setEditSp(sp);
    setEditFlatFee(String(sp.flat_fee_cents / 100));
    setEditRecurringType(sp.recurring_type);
    setEditRate(String(sp.commission_rate));
    setEditSubRate(sp.subscription_commission_rate != null ? String(sp.subscription_commission_rate) : "");
    setEditRxRate(sp.prescription_commission_rate != null ? String(sp.prescription_commission_rate) : "");
    setEditPerTypeRates(sp.subscription_commission_rate != null || sp.prescription_commission_rate != null);
    setEditRecurringFlat(String(sp.recurring_flat_cents / 100));
    setEditTarget(String(sp.monthly_target / 100));
    setEditActive(sp.is_active);
    setEditNotes(sp.notes || "");
  };

  const saveSettings = async () => {
    if (!editSp) return;
    setSaving(true);
    const rate = parseFloat(editRate) || 10;
    const flatFee = Math.round((parseFloat(editFlatFee) || 0) * 100);
    const recurringFlat = Math.round((parseFloat(editRecurringFlat) || 0) * 100);
    const target = Math.round((parseFloat(editTarget) || 0) * 100);

    const payload: any = {
      user_id: editSp.user_id,
      recurring_type: editRecurringType,
      commission_rate: rate,
      subscription_commission_rate: editPerTypeRates ? (parseFloat(editSubRate) || null) : null,
      prescription_commission_rate: editPerTypeRates ? (parseFloat(editRxRate) || null) : null,
      flat_fee_cents: flatFee,
      recurring_flat_cents: recurringFlat,
      monthly_target: target,
      is_active: editActive,
      notes: editNotes.trim() || null,
    };

    const { error } = await supabase.from("salesperson_settings").upsert(payload, { onConflict: "user_id" });

    if (error) {
      toast({ title: "Failed to save", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Settings saved" });
      setEditSp(null);
      fetchData();
    }
    setSaving(false);
  };

  const markPaid = async (payoutId: string) => {
    const { error } = await supabase.from("commission_payouts").update({
      status: "paid",
      paid_at: new Date().toISOString(),
    }).eq("id", payoutId);
    if (error) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Payout marked as paid" });
      fetchData();
    }
  };

  const generatePayout = async (sp: SalespersonRow) => {
    const periodEnd = new Date();
    const periodStart = subDays(periodEnd, 30);

    const { data: orders } = await supabase
      .from("orders")
      .select("amount_cents, commission_rate, status, created_at, order_type")
      .eq("salesperson_id", sp.user_id)
      .gte("created_at", periodStart.toISOString())
      .lte("created_at", periodEnd.toISOString());

    const validOrders = (orders || []).filter((o: any) => !["cancelled", "refunded"].includes(o.status));

    let commissionCents = 0;

    // One-time flat fee component (per new order)
    if (sp.flat_fee_cents > 0) {
      commissionCents += validOrders.length * sp.flat_fee_cents;
    }

    // Recurring component — apply per-order-type rates when available
    switch (sp.recurring_type) {
      case "percentage":
        commissionCents += Math.round(
          validOrders.reduce((s: number, o: any) => {
            let rate = sp.commission_rate;
            if (o.order_type === "subscription" && sp.subscription_commission_rate != null) {
              rate = sp.subscription_commission_rate;
            } else if (o.order_type === "prescription" && sp.prescription_commission_rate != null) {
              rate = sp.prescription_commission_rate;
            }
            return s + (o.amount_cents * rate) / 100;
          }, 0)
        );
        break;
      case "flat":
        commissionCents += validOrders.length * sp.recurring_flat_cents;
        break;
      case "none":
        break;
    }

    if (commissionCents <= 0) {
      toast({ title: "No commissions", description: "No unpaid commissions for this period.", variant: "destructive" });
      return;
    }

    // Build commission structure label
    const structureParts: string[] = [];
    if (sp.flat_fee_cents > 0) structureParts.push(`${fmt(sp.flat_fee_cents)}/order`);
    if (sp.recurring_type === "percentage") {
      if (sp.subscription_commission_rate != null || sp.prescription_commission_rate != null) {
        const subR = sp.subscription_commission_rate ?? sp.commission_rate;
        const rxR = sp.prescription_commission_rate ?? sp.commission_rate;
        structureParts.push(`Sub ${subR}% / Rx ${rxR}%`);
      } else {
        structureParts.push(`${sp.commission_rate}% recurring`);
      }
    }
    if (sp.recurring_type === "flat") structureParts.push(`${fmt(sp.recurring_flat_cents)}/subscriber`);
    const commissionStructure = structureParts.join(" + ") || "none";

    const payload: any = {
      salesperson_id: sp.user_id,
      amount_cents: commissionCents,
      period_start: format(periodStart, "yyyy-MM-dd"),
      period_end: format(periodEnd, "yyyy-MM-dd"),
      status: "pending",
      commission_structure: commissionStructure,
    };

    const { error } = await supabase.from("commission_payouts").insert(payload);

    if (error) {
      toast({ title: "Failed to create payout", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Payout created", description: `${fmt(commissionCents)} payout generated.` });
      fetchData();
    }
  };

  /* ---------- Promo Code helpers ---------- */
  const createPromoCode = async (spId: string) => {
    const code = newPromoCode.trim().toUpperCase();
    if (!code) return;
    const payload: any = {
      code,
      salesperson_id: spId,
      is_active: true,
    };
    if (newPromoRate.trim()) payload.commission_rate = parseFloat(newPromoRate);
    const { error } = await supabase.from("promo_codes").insert(payload);
    if (error) {
      toast({ title: "Failed to create promo code", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Promo code created" });
      setNewPromoCode("");
      setNewPromoRate("");
      fetchData();
    }
  };

  const togglePromoActive = async (id: string, active: boolean) => {
    const { error } = await supabase.from("promo_codes").update({ is_active: !active }).eq("id", id);
    if (error) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    } else {
      fetchData();
    }
  };

  const deletePromo = async (id: string) => {
    const { error } = await supabase.from("promo_codes").delete().eq("id", id);
    if (error) {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Promo code deleted" });
      fetchData();
    }
  };

  const getName = (sp: SalespersonRow) =>
    (sp.first_name || sp.last_name) ? `${sp.first_name || ""} ${sp.last_name || ""}`.trim() : sp.user_id.slice(0, 8) + "…";

  const getCommissionSummary = (sp: SalespersonRow) => {
    const parts: string[] = [];
    if (sp.flat_fee_cents > 0) parts.push(`${fmt(sp.flat_fee_cents)} per order`);
    if (sp.recurring_type === "percentage" && sp.commission_rate > 0) {
      if (sp.subscription_commission_rate != null || sp.prescription_commission_rate != null) {
        const subR = sp.subscription_commission_rate ?? sp.commission_rate;
        const rxR = sp.prescription_commission_rate ?? sp.commission_rate;
        parts.push(`Sub ${subR}% / Rx ${rxR}%`);
      } else {
        parts.push(`${sp.commission_rate}% recurring`);
      }
    }
    if (sp.recurring_type === "flat" && sp.recurring_flat_cents > 0)
      parts.push(`${fmt(sp.recurring_flat_cents)}/subscriber`);
    return parts.length > 0 ? parts.join(" + ") : "Not configured";
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  /* ---------- Promo code management sub-view ---------- */
  if (promoView) {
    const sp = salespeople.find((s) => s.user_id === promoView);
    const spCodes = promoCodes.filter((c) => c.salesperson_id === promoView);
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg">
            Promo Codes — {sp ? getName(sp) : promoView.slice(0, 8)}
          </h3>
          <Button variant="outline" size="sm" onClick={() => setPromoView(null)}>← Back</Button>
        </div>

        {/* Create new */}
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Code</Label>
                <Input
                  placeholder="e.g. SARAH20"
                  value={newPromoCode}
                  onChange={(e) => setNewPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                  className="uppercase"
                />
              </div>
              <div className="w-28 space-y-1">
                <Label className="text-xs">Rate % (optional)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  placeholder="Default"
                  value={newPromoRate}
                  onChange={(e) => setNewPromoRate(e.target.value)}
                />
              </div>
              <Button size="sm" onClick={() => createPromoCode(promoView)} disabled={!newPromoCode.trim()} className="gap-1">
                <Plus size={14} /> Add
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Leave rate blank to use the salesperson's default commission rate. Patients enter this code at checkout.
            </p>
          </CardContent>
        </Card>

        {/* Existing codes */}
        <Card className="border-border">
          <CardContent className="p-0">
            {spCodes.length === 0 ? (
              <p className="text-center text-muted-foreground py-12 text-sm">No promo codes yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Uses</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {spCodes.map((c) => (
                    <TableRow key={c.id} className={!c.is_active ? "opacity-50" : ""}>
                      <TableCell className="font-mono font-semibold">{c.code}</TableCell>
                      <TableCell className="text-sm">{c.commission_rate != null ? `${c.commission_rate}%` : "Default"}</TableCell>
                      <TableCell>{c.usage_count}</TableCell>
                      <TableCell>
                        <Badge variant={c.is_active ? "default" : "secondary"} className="text-xs">
                          {c.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(c.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Copy code"
                          onClick={() => {
                            navigator.clipboard.writeText(c.code);
                            toast({ title: "Copied!" });
                          }}
                        >
                          <Copy size={14} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => togglePromoActive(c.id, c.is_active)}>
                          {c.is_active ? <XCircle size={14} /> : <CheckCircle size={14} />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deletePromo(c.id)}>
                          <Trash2 size={14} className="text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (payoutView) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg">Commission Payouts</h3>
          <Button variant="outline" size="sm" onClick={() => setPayoutView(false)}>← Back to Manage</Button>
        </div>
        <Card className="border-border">
          <CardContent className="p-0">
            {payouts.length === 0 ? (
              <p className="text-center text-muted-foreground py-12 text-sm">No payouts yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Salesperson</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Structure</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Paid At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.salesperson_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(p.period_start), "MMM d")} – {format(new Date(p.period_end), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="font-medium">{fmt(p.amount_cents)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.commission_structure || "—"}</TableCell>
                      <TableCell>
                        <Badge variant={p.status === "paid" ? "default" : "secondary"} className="text-xs capitalize">
                          {p.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.paid_at ? format(new Date(p.paid_at), "MMM d, yyyy") : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {p.status === "pending" && (
                          <Button variant="outline" size="sm" onClick={() => markPaid(p.id)} className="gap-1">
                            <CheckCircle size={12} /> Mark Paid
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <Settings size={16} /> Manage Salespeople
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPayoutView(true)} className="gap-1">
              <DollarSign size={14} /> Payouts
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1">
              <RefreshCw size={14} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {salespeople.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">No salespeople found. Assign the salesperson role from Users & Roles.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Commission Structure</TableHead>
                  <TableHead>Status</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>Clicks</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salespeople.map((sp) => (
                  <TableRow key={sp.user_id} className={!sp.is_active ? "opacity-50" : ""}>
                    <TableCell className="font-medium">{getName(sp)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{sp.email || "—"}</TableCell>
                    <TableCell>
                      <p className="text-sm">{getCommissionSummary(sp)}</p>
                    </TableCell>
                    <TableCell>
                      {sp.is_active ? (
                        <Badge variant="default" className="text-xs gap-1"><CheckCircle size={10} /> Active</Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs gap-1"><XCircle size={10} /> Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>{sp.orderCount}</TableCell>
                    <TableCell>{sp.referralClicks}</TableCell>
                    <TableCell className="font-medium">{fmt(sp.totalRevenue)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(sp)} title="Edit settings">
                        <Edit size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setPromoView(sp.user_id)} title="Promo codes">
                        <Tag size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => generatePayout(sp)} title="Generate payout" className="text-xs gap-1">
                        <DollarSign size={12} /> Payout
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editSp} onOpenChange={(open) => !open && setEditSp(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Edit {editSp ? getName(editSp) : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* One-time flat fee */}
            <div className="space-y-2 rounded-md border border-border p-3">
              <Label className="text-sm font-semibold">One-Time Flat Fee</Label>
              <p className="text-xs text-muted-foreground">Fixed amount earned for each new order. Set to 0 to disable.</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <Input type="number" min={0} step={1} value={editFlatFee} onChange={(e) => setEditFlatFee(e.target.value)} placeholder="0" />
              </div>
            </div>

            {/* Recurring component */}
            <div className="space-y-3 rounded-md border border-border p-3">
              <Label className="text-sm font-semibold">Recurring Commission</Label>
              <p className="text-xs text-muted-foreground">Earned each billing period for retained subscribers.</p>
              <Select value={editRecurringType} onValueChange={(v) => setEditRecurringType(v as RecurringType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="percentage">Percentage of Revenue</SelectItem>
                  <SelectItem value="flat">Flat $ per Subscriber</SelectItem>
                </SelectContent>
              </Select>

              {editRecurringType === "percentage" && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} max={100} step={0.5} value={editRate} onChange={(e) => setEditRate(e.target.value)} placeholder="10" />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">% (default)</span>
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <Switch checked={editPerTypeRates} onCheckedChange={setEditPerTypeRates} id="per-type-toggle" />
                    <Label htmlFor="per-type-toggle" className="text-xs">Different rates by order type</Label>
                  </div>

                  {editPerTypeRates && (
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Subscription %</Label>
                        <Input type="number" min={0} max={100} step={0.5} value={editSubRate} onChange={(e) => setEditSubRate(e.target.value)} placeholder={editRate || "10"} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Prescription %</Label>
                        <Input type="number" min={0} max={100} step={0.5} value={editRxRate} onChange={(e) => setEditRxRate(e.target.value)} placeholder={editRate || "10"} />
                      </div>
                      <p className="col-span-2 text-xs text-muted-foreground">Leave blank to use the default rate above.</p>
                    </div>
                  )}
                </div>
              )}

              {editRecurringType === "flat" && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">$</span>
                  <Input type="number" min={0} step={1} value={editRecurringFlat} onChange={(e) => setEditRecurringFlat(e.target.value)} placeholder="25" />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">/ subscriber</span>
                </div>
              )}
            </div>

            <div>
              <Label>Monthly Target ($)</Label>
              <Input type="number" min={0} step={100} value={editTarget} onChange={(e) => setEditTarget(e.target.value)} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={editActive} onCheckedChange={setEditActive} />
              <Label>Active</Label>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSp(null)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalespersonManagement;
